import { ref, watch } from 'vue'

/**
 * Reactive localStorage wrapper.
 * Behaves like @vueuse/core's useStorage, but without external dependency.
 *
 * @param {string} key - localStorage key
 * @param {any} defaultValue - Default value if nothing is stored
 * @returns {import('vue').Ref} Reactive ref synchronized with localStorage
 */
export function useStorage(key, defaultValue) {
  let initialValue = defaultValue

  try {
    const stored = localStorage.getItem(key)
    if (stored !== null) {
      initialValue = JSON.parse(stored)
    }
  } catch {
    // Fallback to defaultValue on parse errors
  }

  const data = ref(initialValue)

  watch(data, (newValue) => {
    try {
      localStorage.setItem(key, JSON.stringify(newValue))
    } catch {
      console.warn(`[useStorage] Could not write ${key} to localStorage`)
    }
  }, { deep: true })

  // React to changes from other tabs
  window.addEventListener('storage', (event) => {
    if (event.key === key) {
      try {
        data.value = event.newValue ? JSON.parse(event.newValue) : defaultValue
      } catch {
        data.value = defaultValue
      }
    }
  })

  return data
}
